"use client"

import { NewsletterFooterStrip } from "@/api/generated/BMWInterfaces"
import DirectFooterIcon from "@/components/shared/icons/DirectFooterIcon"
import colors from "@/lib/theme/colors"
import { Box, styled, Typography } from "@mui/material"
import { usePathname } from "next/navigation"
import { useState } from "react"
import { HomePageContainer } from "../shared/shared"
import NewsletterForm from "./NewsletterForm"
import NewsletterThankYou from "./NewsletterThankYou"
import {
  NEWSLETTER_HEADING,
  NEWSLETTER_SOURCE_FOOTER,
  NEWSLETTER_SUBTITLE,
} from "./newsletterContent"
import { markRegistered } from "./newsletterSuppression"

const StyledWrapper = styled("section")`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${colors.directBlack};
  max-width: 100vw;
  transform: translateY(1px);
`

const StyledContainer = styled(HomePageContainer, {
  shouldForwardProp: (prop) => prop !== "$isHomePage",
})<{ $isHomePage: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 32px;
  gap: 32px;
  position: relative;
  z-index: 2;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 24px;
    padding: 40px 88px ${({ $isHomePage }) => ($isHomePage ? "32px" : "40px")};
    max-width: calc(1280px + 88px + 88px);
  }
  ${(p) => p.theme.breakpoints.up("lg")} {
    max-width: calc(1440px + 88px + 88px);
  }
`

const StyledContent = styled(Box)`
  border-bottom: 1px solid ${colors.directDisable};
  padding-bottom: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding-bottom: 40px;
  }
`

const ContentLayout = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 0;
  }
`

const MobileLogo = styled("div")`
  display: flex;
  align-self: flex-start;
  padding-bottom: 14px;
  svg {
    width: 90px;
    height: auto;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    display: none;
  }
`

const Headline = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex: 1 0 0;
    min-width: 0;
    gap: 12px;
    padding-inline-end: 32px;
    padding-bottom: 16px;
  }
`

const Heading = styled(Typography)`
  color: ${colors.white};
  font-size: 32px;
  line-height: normal;
  text-align: right;
  letter-spacing: 0;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 48px;
  }
`

const Subtitle = styled(Typography)`
  color: ${colors.white};
  line-height: 26px;
  letter-spacing: 0;
  text-align: right;
  ${(p) => p.theme.breakpoints.up("md")} {
    max-width: 470px;
    padding-top: 2px;
  }
`

const FormCol = styled("div")`
  display: flex;
  width: 100%;
  order: 2;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex: 1 0 0;
    min-width: 552px;
    order: 1;
    padding-top: 34px;
  }
  ${(p) => p.theme.breakpoints.up("xl")} {
    min-width: 720px;
  }
`

interface NewsletterSectionProps {
  content: NewsletterFooterStrip | null
}

const NewsletterSection = ({ content }: NewsletterSectionProps): React.ReactElement => {
  const pathname = usePathname()
  const isHomePage = pathname === "/"
  const [registered, setRegistered] = useState(false)

  const heading = content?.title?.trim() || NEWSLETTER_HEADING
  const subtitle = content?.subtitle?.trim() || NEWSLETTER_SUBTITLE

  const handleSuccess = (): void => {
    markRegistered()
    setRegistered(true)
  }

  return (
    <StyledWrapper>
      <StyledContainer $isHomePage={isHomePage}>
        <StyledContent>
          {registered ? (
            <NewsletterThankYou />
          ) : (
            <ContentLayout>
              <MobileLogo>
                <DirectFooterIcon />
              </MobileLogo>
              <FormCol>
                <NewsletterForm source={NEWSLETTER_SOURCE_FOOTER} onSuccess={handleSuccess} />
              </FormCol>
              <Headline>
                <Heading variant="h2">{heading}</Heading>
                <Subtitle variant="body1">{subtitle}</Subtitle>
              </Headline>
            </ContentLayout>
          )}
        </StyledContent>
      </StyledContainer>
    </StyledWrapper>
  )
}

export default NewsletterSection
